import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

export const ImportLeadsModal = ({ open, onOpenChange, onImportComplete }) => {
  const { get } = useApi();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [template, setTemplate] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [campaignsData, templateData] = await Promise.all([
        get('/campaigns'),
        get('/leads/import/template')
      ]);
      setCampaigns(campaignsData);
      setTemplate(templateData);
      if (campaignsData.length > 0) {
        setSelectedCampaign(campaignsData[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [get]);

  useEffect(() => {
    if (open) {
      loadData();
      setFile(null);
      setImportResult(null);
    }
  }, [open, loadData]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        toast.error('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls)');
        return;
      }
      setFile(selectedFile);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedCampaign) {
      toast.error('Bitte wählen Sie eine Datei und eine Kampagne');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('campaign_id', selectedCampaign);

      const response = await fetch(`${API_URL}/api/leads/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Import fehlgeschlagen');
      }

      setImportResult(result);
      
      if (result.imported > 0) {
        toast.success(`${result.imported} Leads erfolgreich importiert`);
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      toast.error(error.message || 'Fehler beim Import');
      setImportResult({ success: false, error: error.message });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    // Create a simple CSV template for download
    const headers = template?.columns.map(c => c.name).join(';') || '';
    const example = template?.columns.map(c => template.example[c.name] || '').join(';') || '';
    const csvContent = `${headers}\n${example}`;
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lead_import_vorlage.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[#E63946]" />
            Leads aus Excel importieren
          </DialogTitle>
          <DialogDescription>
            Laden Sie eine Excel-Datei mit Lead-Daten hoch
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Campaign Selection */}
          <div className="space-y-2">
            <Label>Ziel-Kampagne *</Label>
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger data-testid="import-campaign-select">
                <SelectValue placeholder="Kampagne auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Alle importierten Leads werden dieser Kampagne zugeordnet
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Excel-Datei *</Label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#E63946] transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="excel-upload"
                data-testid="excel-file-input"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span>{file.name}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-gray-400" />
                    <p className="text-gray-600">Klicken Sie hier oder ziehen Sie eine Datei</p>
                    <p className="text-xs text-gray-400">Unterstützte Formate: .xlsx, .xls</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Template Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">Erforderliche Spalten:</span>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Vorlage herunterladen
              </Button>
            </div>
            {template && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                {template.columns.map(col => (
                  <div key={col.name} className="flex items-center gap-2">
                    {col.required ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      <span className="text-gray-300">○</span>
                    )}
                    <span className={col.required ? 'font-medium' : 'text-gray-600'}>
                      {col.name}
                    </span>
                    <span className="text-gray-400 text-xs">- {col.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Import Result */}
          {importResult && (
            <div className={`rounded-lg p-4 ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-3">
                {importResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {importResult.success ? importResult.message : 'Import fehlgeschlagen'}
                  </p>
                  {importResult.success && (
                    <div className="mt-2 text-sm text-green-700">
                      <p>✓ {importResult.imported} Leads importiert</p>
                      {importResult.skipped > 0 && (
                        <p>○ {importResult.skipped} Zeilen übersprungen</p>
                      )}
                    </div>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2 text-sm text-red-700">
                      <p className="font-medium">Fehler:</p>
                      <ul className="list-disc list-inside">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {importResult.error && (
                    <p className="mt-1 text-sm text-red-700">{importResult.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {importResult?.success ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!importResult?.success && (
            <Button
              onClick={handleImport}
              disabled={!file || !selectedCampaign || importing}
              className="bg-[#E63946] hover:bg-[#D62839]"
              data-testid="import-submit-button"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importiere...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importieren
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
